import { registerSW } from "virtual:pwa-register";

export interface PwaSnapshot {
  offlineReady: boolean;
  updateAvailable: boolean;
  registrationError: string | null;
}

type Listener = () => void;
type UpdateServiceWorker = (reloadPage?: boolean) => Promise<void>;

const listeners = new Set<Listener>();
let initialized = false;
let updateServiceWorker: UpdateServiceWorker | null = null;
let snapshot: PwaSnapshot = {
  offlineReady: false,
  updateAvailable: false,
  registrationError: null,
};

function publish(next: Partial<PwaSnapshot>) {
  snapshot = { ...snapshot, ...next };
  listeners.forEach((listener) => listener());
}

export function initializePwa() {
  if (initialized || !import.meta.env.PROD || !("serviceWorker" in navigator)) {
    return;
  }

  initialized = true;
  updateServiceWorker = registerSW({
    immediate: true,
    onOfflineReady: () => publish({ offlineReady: true }),
    onNeedRefresh: notifyPwaUpdateAvailable,
    onRegisterError: (error) => {
      console.error("PWA 注册失败", error);
      publish({ registrationError: "离线缓存初始化失败，建议联网后重试。" });
    },
  });
}

export function subscribePwa(listener: Listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getPwaSnapshot() {
  return snapshot;
}

export function notifyPwaUpdateAvailable() {
  publish({ updateAvailable: true, registrationError: null });
}

export function dismissPwaNotice() {
  publish({ offlineReady: false, updateAvailable: false, registrationError: null });
}

export async function applyPwaUpdate(
  serviceWorker: UpdateServiceWorker | null = updateServiceWorker,
): Promise<boolean> {
  if (!serviceWorker) {
    publish({
      updateAvailable: false,
      registrationError: "更新服务尚未就绪。请先保存输入，稍后手动刷新页面。",
    });
    return false;
  }
  try {
    await serviceWorker(true);
    publish({ updateAvailable: false, registrationError: null });
    return true;
  } catch (error) {
    console.error("PWA 更新失败", error);
    publish({
      updateAvailable: false,
      registrationError: "自动更新失败。当前输入不会被清除；请保存后手动刷新页面。",
    });
    return false;
  }
}
